// ========================================
// UTILIDAD DE COMPRESIÓN DE IMÁGENES
// Comprime imágenes client-side antes de subir
// Reduce archivos de 5MB+ a ~500KB manteniendo calidad
// ========================================

/**
 * Configuraciones de compresión según el tipo de contenido
 */
const COMPRESSION_CONFIGS = {
  // Fotos generales (cámaras móviles)
  photo: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    targetSizeKB: 500,
    format: 'image/jpeg'
  },
  
  // Documentos escaneados (necesitan mayor resolución)
  document: {
    maxWidth: 1600,
    maxHeight: 2000,
    quality: 0.9,
    targetSizeKB: 800,
    format: 'image/jpeg'
  },
  
  // Imágenes pequeñas (avatares, logos)
  thumbnail: {
    maxWidth: 800,
    maxHeight: 800,
    quality: 0.85,
    targetSizeKB: 200,
    format: 'image/jpeg'
  }
};

/**
 * Detecta el tipo de contenido de la imagen basado en sus dimensiones
 * @param {number} width - Ancho de la imagen
 * @param {number} height - Alto de la imagen
 * @returns {string} Tipo de configuración a usar
 */
function detectImageType(width, height) {
  const aspectRatio = width / height;
  
  // Documentos suelen ser verticales (altura > ancho)
  if (aspectRatio < 0.8 && height > 1500) {
    return 'document';
  }
  
  // Imágenes pequeñas
  if (width <= 800 && height <= 800) {
    return 'thumbnail';
  }
  
  // Por defecto, tratar como foto
  return 'photo';
}

/**
 * Comprime una imagen usando Canvas API
 * @param {File} file - Archivo de imagen original
 * @param {Object} config - Configuración de compresión
 * @returns {Promise<File>} Archivo comprimido
 */
function compressImage(file, config) {
  return new Promise((resolve, reject) => {
    // Verificar que es una imagen
    if (!file.type.startsWith('image/')) {
      resolve(file); // No es imagen, devolver original
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = function() {
      try {
        // Detectar tipo de imagen automáticamente si no se especificó
        const imageType = detectImageType(img.width, img.height);
        const finalConfig = config || COMPRESSION_CONFIGS[imageType];

        // Calcular nuevas dimensiones manteniendo aspect ratio
        let { width, height } = calculateDimensions(
          img.width, 
          img.height, 
          finalConfig.maxWidth, 
          finalConfig.maxHeight
        );

        // Configurar canvas
        canvas.width = width;
        canvas.height = height;

        // Dibujar imagen redimensionada
        ctx.fillStyle = '#FFFFFF'; // Fondo blanco para JPEGs
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Comprimir con calidad ajustable
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir imagen'));
              return;
            }

            // Crear nuevo File object manteniendo metadata
            const compressedFile = new File([blob], file.name, {
              type: finalConfig.format,
              lastModified: Date.now()
            });

            resolve(compressedFile);
          },
          finalConfig.format,
          finalConfig.quality
        );

      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Error al cargar imagen'));
    };

    // Cargar imagen
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calcula nuevas dimensiones manteniendo aspect ratio
 * @param {number} srcWidth - Ancho original
 * @param {number} srcHeight - Alto original
 * @param {number} maxWidth - Ancho máximo
 * @param {number} maxHeight - Alto máximo
 * @returns {Object} Nuevas dimensiones {width, height}
 */
function calculateDimensions(srcWidth, srcHeight, maxWidth, maxHeight) {
  let width = srcWidth;
  let height = srcHeight;

  // Reducir si excede el ancho máximo
  if (width > maxWidth) {
    height = height * (maxWidth / width);
    width = maxWidth;
  }

  // Reducir si excede el alto máximo
  if (height > maxHeight) {
    width = width * (maxHeight / height);
    height = maxHeight;
  }

  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * Comprime múltiples imágenes en paralelo
 * @param {FileList|Array} files - Lista de archivos
 * @param {Object} config - Configuración opcional
 * @returns {Promise<Array>} Array de archivos comprimidos
 */
function compressMultipleImages(files, config) {
  const fileArray = Array.from(files);
  
  return Promise.all(
    fileArray.map(file => compressImage(file, config))
  );
}

/**
 * Función principal para comprimir archivos de Beewo
 * Mantiene compatibilidad con archivos no-imagen
 * @param {File} file - Archivo a procesar
 * @param {string} compressionType - Tipo de compresión ('photo', 'document', 'thumbnail')
 * @returns {Promise<File>} Archivo procesado
 */
export async function compressFileForBeewo(file, compressionType = null) {
  try {
    // Log inicial
    console.log(`🗜️ Comprimiendo: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    // Si no es imagen, devolver sin modificar
    if (!file.type.startsWith('image/')) {
      console.log(`📄 No es imagen, enviando original`);
      return file;
    }

    // Obtener configuración
    const config = compressionType ? 
      COMPRESSION_CONFIGS[compressionType] : 
      null; // Auto-detect

    // Comprimir
    const compressedFile = await compressImage(file, config);
    
    // Log resultado
    const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
    const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
    const reduction = (((file.size - compressedFile.size) / file.size) * 100).toFixed(1);
    
    console.log(`✅ Compresión completada:`);
    console.log(`   Original: ${originalSizeMB}MB → Comprimido: ${compressedSizeMB}MB`);
    console.log(`   Reducción: ${reduction}%`);

    return compressedFile;

  } catch (error) {
    console.error('❌ Error en compresión:', error);
    console.log('🔄 Usando archivo original como fallback');
    return file; // Fallback al archivo original
  }
}

/**
 * Función para validar archivo antes o después de compresión
 * @param {File} file - Archivo a validar
 * @param {Object} options - Opciones de validación
 * @param {boolean} options.checkSizeLimit - Si validar límite de 100MB (default: true)
 * @param {boolean} options.checkFileType - Si validar tipo de archivo (default: true)
 * @returns {Object} Resultado de validación {isValid, message, file}
 */
export function validateFileForBeewo(file, options = {}) {
  const { checkSizeLimit = true, checkFileType = true, allowAllTypes = true } = options;
  const maxSize = 104857600; // 100MB en bytes
  
  // Validar tipo de archivo (opcional - por defecto permite todos los tipos)
  if (checkFileType && !allowAllTypes) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const fileType = file.type.toLowerCase();
    
    if (!allowedTypes.includes(fileType)) {
      return {
        isValid: false,
        message: "Tipo de archivo no permitido. Solo se aceptan imágenes (JPG, PNG, GIF, WEBP) y PDFs",
        file: null
      };
    }
  }
  
  // Nota: Con allowAllTypes = true (por defecto), se permiten todos los tipos de archivo
  
  // Validar tamaño (solo si se solicita)
  if (checkSizeLimit && file.size > maxSize) {
    return {
      isValid: false,
      message: "El archivo excede el límite de 100 MB permitido. Te recomendamos utilizar una pregunta de tipo link para este tipo de archivos",
      file: null
    };
  }

  return {
    isValid: true,
    message: "",
    file: file
  };
}

// Exportar configuraciones para uso externo si es necesario
export { COMPRESSION_CONFIGS };